import { WidgetsByStandard } from '@atlas-metrics/microsite-types';
import * as Sentry from '@sentry/nextjs';
import axios, { AxiosError } from 'axios';
import mixpanel from 'mixpanel-browser';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import * as React from 'react';
import { useState } from 'react';

import ComingSoon from '@/components/ComingSoon';
import NotFound from '@/components/NotFound';
import OrganizationComponent from '@/components/Organization';
import Password from '@/components/Password';
import Seo from '@/components/Seo';

import { DEFAULT_LANGUAGE } from '..';

import { Organization, Projects } from '@/types';

type OrganizationPageProps = {
  code: number;
  slug: string;
  year?: string;
  widgets: WidgetsByStandard[];
  organization: Organization;
  mixPanelToken?: string;
};

const buildWidgetUrl = (slug: string, year?: string) => {
  const requestUrl = new URL(`${process.env.API_GW_ENDPOINT}/widgets/${slug}`);
  if (year) {
    requestUrl.searchParams.append('year', year);
  }
  return requestUrl;
};

const buildProjectUrl = (slug: string, year: string) => {
  const requestUrl = new URL(`${process.env.API_GW_ENDPOINT}/projects`);
  requestUrl.searchParams.append('organizationSlug', slug);
  requestUrl.searchParams.append('reportingYear', year);

  return requestUrl;
};

export default function OrganizationPage(props: OrganizationPageProps) {
  React.useEffect(() => {
    props.mixPanelToken?.length &&
      mixpanel.init(props.mixPanelToken, {
        track_pageview: true,
        persistence: 'localStorage',
      });
  }, [props.mixPanelToken]);

  const router = useRouter();
  const { t } = useTranslation();
  const [loadingYear, setLoadingYear] = useState<null | string>(null);
  const [password, setPassword] = useState('');
  const [isPasswordIncorrect, setIsPasswordIncorrect] = useState(false);
  const [code, setCode] = useState<number>(props.code);
  const [organization, setOrganization] = useState<Organization | null>(
    props.organization
  );
  const [widgets, setWidgets] = useState<WidgetsByStandard[] | null>(
    props.widgets
  );

  const [projects, setProjects] = useState<Projects | null>(null);

  React.useEffect(() => {
    if (code !== 200 || !organization) return;

    const year = organization.reportPeriods?.find(
      (current) => current.id === organization.selectedReportPeriod
    )?.name;

    if (!year || projects?.project?.reportingYear === parseInt(year)) return;

    axios
      .get(buildProjectUrl(props.slug, year).toString())
      .then((response) => {
        setProjects(response.data?.project?.title ? response.data : null);
      })
      .catch((err) => {
        Sentry.captureException(err);
        setProjects(null);
      });
  }, [code, organization, props.slug, projects]);

  const handleSubmit = async (year?: string) => {
    setLoadingYear(year ?? null);
    try {
      setIsPasswordIncorrect(false); // reset before submit again

      const result = await axios.get(
        buildWidgetUrl(props.slug, year).toString(),
        {
          headers: {
            Authorization: password,
          },
        }
      );

      setCode(result.status);
      setIsPasswordIncorrect(false);

      if (result.status !== 200 || !result.data) return;

      const { organization, widgets } = result.data;

      organization && setOrganization(organization);
      widgets && setWidgets(widgets);
      window.scrollTo(0, 0);
      router.push(`/org/${props.slug}${year ? '/' + year : ''}`, undefined, {
        shallow: true,
      });
    } catch (err) {
      const response = (err as AxiosError).response;
      setCode(response?.status ?? 500);
      setIsPasswordIncorrect(response?.status === 401);
    } finally {
      setLoadingYear(null);
    }
  };

  if (code === 404 || code === 500 || code === 405) {
    return (
      <>
        <Seo templateTitle={t('not-found.name')} />
        <NotFound />
      </>
    );
  }

  if (code === 401) {
    return (
      <>
        <Seo
          templateTitle={props.organization?.name ?? t('password.required')}
        />
        <Password
          handleSubmit={() => handleSubmit(props.year)}
          password={password}
          setPassword={setPassword}
          isPasswordIncorrect={isPasswordIncorrect}
          setIsPasswordIncorrect={setIsPasswordIncorrect}
        />
      </>
    );
  }

  if (
    organization == null ||
    !organization.reportPeriods ||
    organization.reportPeriods.length === 0
  ) {
    return (
      <>
        <Seo templateTitle={t('coming-soon.title')} />
        <ComingSoon />
      </>
    );
  }

  return (
    <>
      <Seo templateTitle={organization?.name ?? t('unknown-organization')} />
      <OrganizationComponent
        switchReportPeriod={handleSubmit}
        organization={organization}
        widgets={widgets}
        loadingYear={loadingYear}
        projects={projects}
      />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const [slug, year] =
    ctx.params?.slug != null && Array.isArray(ctx.params.slug)
      ? ctx.params.slug
      : [ctx.params?.slug];

  const defaultLanguage =
    (ctx.req.headers.browserlocale as string) ?? ctx.locale ?? DEFAULT_LANGUAGE;

  const props = {
    slug,
  };

  if (!slug) {
    return {
      props: {
        ...props,
        ...(await serverSideTranslations(defaultLanguage, ['common'])),
        code: 404,
      },
    };
  }

  Sentry.setUser({ id: slug });
  try {
    const widgetUrl = buildWidgetUrl(slug, year);
    const result = await axios.get(widgetUrl.toString());

    return {
      props: {
        ...props,
        ...(await serverSideTranslations(
          result.data?.organization?.language ?? DEFAULT_LANGUAGE,
          ['common']
        )),
        code: result.status,
        ...(year && { year }),
        ...result.data,
        mixPanelToken: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN ?? '',
      },
    };
  } catch (err) {
    const response = (err as AxiosError).response;
    if (response?.status === 401) {
      const data = response.data as {
        error: string;
        organization: { language: string; name: string };
      };
      return {
        props: {
          ...props,
          ...(await serverSideTranslations(
            data.organization?.language ?? DEFAULT_LANGUAGE,
            ['common']
          )),
          code: response.status,
          ...(year && { year }),
          ...data,
          mixPanelToken: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN ?? '',
        },
      };
    }

    if (response?.status !== 200 && response?.status !== 401) {
      return {
        props: {
          ...props,
          ...(await serverSideTranslations(defaultLanguage, ['common'])),
          code: response?.status,
          ...(year && { year }),
        },
      };
    }

    return {
      props: {
        ...(await serverSideTranslations(DEFAULT_LANGUAGE, ['common'])),
        organization: {},
        code: 500,
      },
    };
  }
};
